import React, { useState, useEffect, useRef } from 'react';

const extractRecetaInfo = (grupo) => {
    let info = grupo?.recetaInfo || {};
    if ((info.W === undefined && info.w === undefined) && grupo?.items?.[0]) {
        const raw = grupo.items[0].medidasReceta || grupo.items[0].MedidasReceta;
        if (raw) {
            try {
                const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
                info = { ...parsed, ...info };
            } catch (e) {
                console.error("Error parsing item medidasReceta", e);
            }
        }
    }
    return info;
};

const getW = (obj) => obj?.W !== undefined && obj?.W !== null ? String(obj.W) : (obj?.w !== undefined && obj?.w !== null ? String(obj.w) : '');
const getH = (obj) => obj?.H !== undefined && obj?.H !== null ? String(obj.H) : (obj?.h !== undefined && obj?.h !== null ? String(obj.h) : '');
const getCant = (obj) => obj?.Cantidad !== undefined && obj?.Cantidad !== null ? String(obj.Cantidad) : (obj?.cantidad !== undefined && obj?.cantidad !== null ? String(obj.cantidad) : '1');

const ModalRecetaCartSVG = ({ grupo, onCancel, onGuardarCambiosReceta, onAplicarManoDeObra }) => {
    const info = extractRecetaInfo(grupo);

    const [medidaW, setMedidaW] = useState(getW(info));
    const [medidaH, setMedidaH] = useState(getH(info));
    const [medidaCantidad, setMedidaCantidad] = useState(getCant(info));
    const [montoManoDeObra, setMontoManoDeObra] = useState(grupo?.montoManoDeObra !== undefined && grupo.montoManoDeObra !== null ? String(grupo.montoManoDeObra) : '');
    const [recetaTemplate, setRecetaTemplate] = useState(null);
    const [isApplying, setIsApplying] = useState(false);
    const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (!grupo) return;
        const currentInfo = extractRecetaInfo(grupo);
        const wVal = getW(currentInfo);
        const hVal = getH(currentInfo);
        const cantVal = getCant(currentInfo);
        const moVal = grupo.montoManoDeObra !== undefined && grupo.montoManoDeObra !== null ? String(grupo.montoManoDeObra) : '';

        console.log("%c[ModalRecetaCartSVG] >> Montado / Actualizado con grupo:", "background: #28a745; color: white; font-weight: bold; padding: 2px 5px;");
        console.log("Grupo recibido:", grupo);
        console.log("Info extraída:", currentInfo);
        console.log(`Valores iniciales modal -> W: "${wVal}", H: "${hVal}", Cantidad: "${cantVal}", MO: "${moVal}"`);

        setMedidaW(wVal);
        setMedidaH(hVal);
        setMedidaCantidad(cantVal);
        setMontoManoDeObra(moVal);

        const idReceta = grupo?.items?.[0]?.idReceta || grupo?.items?.[0]?.IdReceta || currentInfo.IdReceta;
        console.log("IdReceta detectado:", idReceta);

        let isCancelled = false;

        if (idReceta) {
            setIsLoadingTemplate(true);
            fetch(`/api/vidrieria/recetas/${idReceta}`)
                .then(res => res.ok ? res.json() : null)
                .then(data => {
                    if (isCancelled || !isMountedRef.current) return;
                    console.log("[ModalRecetaCartSVG] Plantilla de receta obtenida:", data);
                    if (data && data.detalles) {
                        setRecetaTemplate(data);
                    }
                })
                .catch(err => {
                    if (isCancelled || !isMountedRef.current) return;
                    console.error("Error al cargar plantilla de receta:", err);
                })
                .finally(() => {
                    if (!isCancelled && isMountedRef.current) {
                        setIsLoadingTemplate(false);
                    }
                });
        } else {
            setRecetaTemplate(null);
        }

        return () => {
            isCancelled = true;
        };
    }, [grupo]);

    if (!grupo) return null;

    const currentW = medidaW !== '' ? (parseFloat(medidaW) || 0) : (parseFloat(info.W || info.w) || 0);
    const currentH = medidaH !== '' ? (parseFloat(medidaH) || 0) : (parseFloat(info.H || info.h) || 0);
    const currentCantVentanas = medidaCantidad !== '' ? (parseFloat(medidaCantidad) || 0) : (parseFloat(info.Cantidad || info.cantidad) || 1);

    // Calcular las cantidades recalculadas para cada item
    const itemsRecalculados = (grupo.items || []).map(item => {
        let cantCalculada = parseFloat(item.Cantidad ?? item.cantidad ?? 1);

        if (recetaTemplate && recetaTemplate.detalles && currentW > 0 && currentH > 0) {
            const matchingDetalles = recetaTemplate.detalles.filter(d => String(d.IdPresentacion) === String(item.IdPresentacion));
            if (matchingDetalles.length > 0) {
                cantCalculada = matchingDetalles.reduce((sum, d) => {
                    const cfg = typeof d.ConfiguracionJSON === 'string' ? JSON.parse(d.ConfiguracionJSON) : (d.ConfiguracionJSON || {});
                    const varValue = cfg.variable === 'H' ? currentH : (cfg.variable === 'W' ? currentW : 1);
                    const cantBase = parseFloat(cfg.cantidad) || 1;
                    return sum + (cantBase * varValue * currentCantVentanas);
                }, 0);
            }
        } else if (currentW > 0 && currentH > 0 && parseFloat(info.W) > 0 && parseFloat(info.H) > 0) {
            // Fallback proporcional
            const factorW = currentW / parseFloat(info.W);
            const factorH = currentH / parseFloat(info.H);
            const factorCant = currentCantVentanas / (parseFloat(info.Cantidad) || 1);
            const desc = (item.descripcion || '').toUpperCase();
            const factor = desc.includes('VIDRIO') ? (factorW * factorH) : ((factorW + factorH) / 2);
            cantCalculada = parseFloat(item.Cantidad ?? item.cantidad ?? 1) * factor * factorCant;
        }

        const cantFinal = Number(cantCalculada.toFixed(3));
        const bp = item.precioBase !== undefined ? item.precioBase : parseFloat(item.PrecioVenta !== undefined ? item.PrecioVenta : (item.Precio || 0));
        const baseTotal = bp * cantFinal;

        return {
            ...item,
            precioBase: bp,
            nuevaCantidad: cantFinal,
            baseTotal: baseTotal
        };
    });

    const costoMateriales = itemsRecalculados.reduce((acc, it) => acc + it.baseTotal, 0);
    const mo = parseFloat(montoManoDeObra) || 0;
    const costoTotalConMO = costoMateriales + mo;

    const handleSave = async () => {
        // Distribuir la mano de obra asegurando que la suma de (nuevoPrecioVenta * nuevaCantidad) coincida exactamente con costoTotalConMO
        let acumuladoTotalCalculado = 0;
        const totalItemsCount = itemsRecalculados.length;

        const recalculatedWithPrices = itemsRecalculados.map((it, idx) => {
            const prop = costoMateriales > 0 ? (it.baseTotal / costoMateriales) : (1 / (totalItemsCount || 1));
            let itemMO = mo * prop;
            let itemUnitMO = it.nuevaCantidad > 0 ? (itemMO / it.nuevaCantidad) : itemMO;
            let nuevoPrecioVenta = Number((it.precioBase + itemUnitMO).toFixed(2));

            // Para el último item, ajustar cualquier pequeña discrepancia por redondeo de centavos
            if (idx === totalItemsCount - 1 && mo > 0 && it.nuevaCantidad > 0) {
                const totalEsperado = Number(costoTotalConMO.toFixed(2));
                const diferencia = totalEsperado - (acumuladoTotalCalculado + Number((nuevoPrecioVenta * it.nuevaCantidad).toFixed(2)));
                if (Math.abs(diferencia) >= 0.01 && Math.abs(diferencia) <= 0.05) {
                    const ajusteUnitario = Number((diferencia / it.nuevaCantidad).toFixed(2));
                    nuevoPrecioVenta = Number((nuevoPrecioVenta + ajusteUnitario).toFixed(2));
                    itemUnitMO = Number((nuevoPrecioVenta - it.precioBase).toFixed(2));
                }
            }

            acumuladoTotalCalculado += Number((nuevoPrecioVenta * it.nuevaCantidad).toFixed(2));

            return {
                item: it,
                nuevaCantidad: it.nuevaCantidad,
                nuevoPrecioVenta: nuevoPrecioVenta,
                moPorUnidad: itemUnitMO
            };
        });

        const payload = {
            W: currentW,
            H: currentH,
            Cantidad: currentCantVentanas,
            montoManoDeObra: mo,
            itemsRecalculados: recalculatedWithPrices
        };

        console.log("%c[ModalRecetaCartSVG] >> Guardando cambios de receta:", "background: #007bff; color: white; font-weight: bold; padding: 2px 5px;");
        console.log("Payload a guardar:", payload);
        console.log("Costo Materiales Base:", costoMateriales);
        console.log("Mano de Obra (MO):", mo);
        console.log("Costo Total con MO:", costoTotalConMO);
        console.table(recalculatedWithPrices.map(r => ({
            Producto: r.item?.descripcion || r.item?.Nombre || `ID ${r.item?.IdPresentacion}`,
            IdPresentacion: r.item?.IdPresentacion,
            IdUnidad: r.item?.IdUnidad,
            Cantidad: r.nuevaCantidad,
            PrecioVenta: r.nuevoPrecioVenta,
            MOPorUnidad: r.moPorUnidad,
            Subtotal: Number((r.nuevaCantidad * r.nuevoPrecioVenta).toFixed(2))
        })));

        setIsApplying(true);
        try {
            if (onGuardarCambiosReceta) {
                await onGuardarCambiosReceta(grupo, payload);
            } else if (onAplicarManoDeObra) {
                await onAplicarManoDeObra(grupo, mo);
            }
        } finally {
            if (isMountedRef.current) {
                setIsApplying(false);
            }
        }
    };

    const svgContent = info.SvgBase || recetaTemplate?.SvgBase;

    return (
        <div className="m-3">
            <div className="row form-group col-12 d-flex justify-content-between align-items-center">
                <h5>Detalles: {info.Nombre || recetaTemplate?.Nombre || 'Ventana'}</h5>
                {isLoadingTemplate && (
                    <small className="text-muted">
                        <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                        Cargando fórmulas de receta...
                    </small>
                )}
            </div>

            <div className="row">
                {/* Gráfico SVG y resumen */}
                <div className="col-12 col-md-5 d-flex flex-column align-items-center justify-content-center p-3 mb-3" style={{ backgroundColor: '#f4f6f9', borderRadius: '8px', minHeight: '320px' }}>
                    {svgContent ? (
                        <div
                            className="w-100 h-100 d-flex align-items-center justify-content-center"
                            dangerouslySetInnerHTML={{ __html: svgContent }}
                        />
                    ) : (
                        <div className="text-muted">Sin diseño disponible</div>
                    )}
                    <div className="mt-2 text-center font-weight-bold" style={{ color: '#01a59c', fontSize: '13px' }}>
                        Medidas actuales: W: {currentW}m x H: {currentH}m | Cantidad: {currentCantVentanas} ud.
                    </div>
                </div>

                {/* Controles y desglose */}
                <div className="col-12 col-md-7">
                    {/* Sección 1: Editar Medidas y Recalcular */}
                    <div className="card p-3 mb-3 border-info" style={{ backgroundColor: '#eef9f8' }}>
                        <h6 className="mb-2 font-weight-bold" style={{ color: '#01a59c' }}>
                            <i className="fas fa-ruler-combined mr-1"></i> Modificar Medidas y Cantidad (Recalcular)
                        </h6>
                        <div className="row">
                            <div className="col-12 col-sm-4 form-group mb-2">
                                <label className="mb-1" style={{ fontSize: '12px', fontWeight: 'bold' }}>Ancho (W) [m]</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    placeholder="1.50"
                                    className="form-control form-control-sm font-weight-bold"
                                    value={medidaW}
                                    onChange={(e) => setMedidaW(e.target.value)}
                                    onClick={(e) => e.target.select()}
                                />
                            </div>
                            <div className="col-12 col-sm-4 form-group mb-2">
                                <label className="mb-1" style={{ fontSize: '12px', fontWeight: 'bold' }}>Alto (H) [m]</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    placeholder="2.10"
                                    className="form-control form-control-sm font-weight-bold"
                                    value={medidaH}
                                    onChange={(e) => setMedidaH(e.target.value)}
                                    onClick={(e) => e.target.select()}
                                />
                            </div>
                            <div className="col-12 col-sm-4 form-group mb-2">
                                <label className="mb-1" style={{ fontSize: '12px', fontWeight: 'bold' }}>Cantidad (uds.)</label>
                                <input
                                    type="number"
                                    step="1"
                                    min="1"
                                    placeholder="1"
                                    className="form-control form-control-sm font-weight-bold"
                                    value={medidaCantidad}
                                    onChange={(e) => setMedidaCantidad(e.target.value)}
                                    onClick={(e) => e.target.select()}
                                />
                            </div>
                        </div>
                        <small className="text-muted" style={{ fontSize: '11px' }}>
                            * Al cambiar los valores de Ancho, Alto o Cantidad, las cantidades de los insumos se recalculan automáticamente.
                        </small>
                    </div>

                    {/* Sección 2: Mano de Obra */}
                    <div className="card p-3 mb-3 border-warning" style={{ backgroundColor: '#fff9e6' }}>
                        <div className="d-flex align-items-center justify-content-between flex-wrap" style={{ gap: '10px' }}>
                            <div>
                                <h6 className="mb-0 font-weight-bold" style={{ color: '#856404' }}>
                                    <i className="fas fa-tools mr-1"></i> Mano de Obra de la Receta
                                </h6>
                                <small className="text-muted">Se distribuye proporcionalmente al costo de cada insumo</small>
                            </div>
                            <div className="input-group input-group-sm" style={{ width: '150px' }}>
                                <div className="input-group-prepend">
                                    <span className="input-group-text font-weight-bold" style={{ backgroundColor: '#ffc107', color: '#212529' }}>S/</span>
                                </div>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    placeholder="0.00"
                                    className="form-control form-control-sm text-right font-weight-bold"
                                    style={{ height: '31px', fontSize: '13px' }}
                                    value={montoManoDeObra}
                                    onClick={(e) => e.target.select()}
                                    onChange={(e) => setMontoManoDeObra(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 style={{ color: '#01a59c', fontWeight: 'bold', margin: 0 }}>Desglose de Productos Recalculados</h6>
                    </div>

                    <div className="table-responsive" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                        <table className="table table-sm table-striped" style={{ fontSize: '13px' }}>
                            <thead className="thead-light">
                                <tr>
                                    <th>Producto</th>
                                    <th className="text-center">Cant.</th>
                                    <th className="text-right">Precio Unit.</th>
                                    <th className="text-right">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {itemsRecalculados.map((item, idx) => {
                                    const cant = item.nuevaCantidad;
                                    const bp = item.precioBase;
                                    const prop = costoMateriales > 0 ? (item.baseTotal / costoMateriales) : (1 / (itemsRecalculados.length || 1));
                                    const itemMO = mo * prop;
                                    const itemUnitMO = cant > 0 ? (itemMO / cant) : itemMO;
                                    const unitPriceWithMO = bp + itemUnitMO;
                                    const itemTotalWithMO = unitPriceWithMO * cant;

                                    return (
                                        <tr key={idx}>
                                            <td>
                                                <div>{item.descripcion} {item.unidadMedida}</div>
                                                {mo > 0 && (
                                                    <small className="text-muted" style={{ fontSize: '11px' }}>
                                                        Base: S/ {bp.toFixed(2)} + M.O: S/ {itemUnitMO.toFixed(2)}
                                                    </small>
                                                )}
                                            </td>
                                            <td className="text-center align-middle">
                                                <span className="badge badge-info" style={{ fontSize: '12px' }}>
                                                    {cant}
                                                </span>
                                            </td>
                                            <td className="text-right align-middle font-weight-bold">
                                                S/ {unitPriceWithMO.toFixed(2)}
                                            </td>
                                            <td className="text-right align-middle font-weight-bold" style={{ color: mo > 0 ? '#b78103' : '#333' }}>
                                                S/ {itemTotalWithMO.toFixed(2)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan="3" className="text-right text-muted">Costo Base Materiales:</td>
                                    <td className="text-right font-weight-bold text-muted">S/ {costoMateriales.toFixed(2)}</td>
                                </tr>
                                {mo > 0 && (
                                    <tr style={{ backgroundColor: '#fff3cd', color: '#856404' }}>
                                        <td colSpan="3" className="text-right font-weight-bold">
                                            <i className="fas fa-tools mr-1"></i> Mano de Obra:
                                        </td>
                                        <td className="text-right font-weight-bold">+ S/ {mo.toFixed(2)}</td>
                                    </tr>
                                )}
                                <tr style={{ backgroundColor: '#034b2f', color: 'white' }}>
                                    <td colSpan="3" className="text-right font-weight-bold" style={{ fontSize: '14px' }}>COSTO TOTAL:</td>
                                    <td className="text-right font-weight-bold" style={{ fontSize: '14px' }}>S/ {costoTotalConMO.toFixed(2)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>

            <div className="row mt-3">
                <div className="col-12 text-right">
                    <button type="button" className="btn btn-secondary mr-2" onClick={onCancel} disabled={isApplying}>
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="btn btn-success font-weight-bold shadow-sm"
                        onClick={handleSave}
                        disabled={isApplying || currentW <= 0 || currentH <= 0}
                    >
                        {isApplying ? (
                            <>
                                <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                                Aplicando Cambios...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-check mr-1"></i> Guardar Cambios y Aplicar
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ModalRecetaCartSVG;
